import { useEffect, useRef } from 'react'

/**
 * Network-aware polling hook с exponential backoff.
 *
 * Проблема которую решает: раньше polling (loadFeatures каждые 60с,
 * checkUnreadMessages каждые 30с) бил по API даже когда юзер был
 * offline. У одного юзера в логах накопился запрос с таймаутом
 * 2 660 283 ms = ~44 минуты — interval бил каждые 30с в течение
 * больше часа отсутствия сети, axios копил pending promises.
 *
 * Этот хук:
 *  1. Пропускает tick если navigator.onLine === false
 *  2. Применяет exponential backoff на consecutive failures
 *     (interval × 2 за каждый fail, capped at 5 минут)
 *  3. Сбрасывает backoff на success или 'online' event
 *  4. Listenит online/offline события window — мгновенно
 *     возобновляет poll когда сеть вернулась
 *  5. Чистит interval на unmount или enabled=false
 *
 * @param {Function} callback - async функция которая делает API-вызов.
 *   Должна throw'ить на ошибке — это сигнал для backoff.
 * @param {number} intervalMs - базовый интервал в миллисекундах
 * @param {Object} opts
 * @param {boolean} opts.enabled - если false, polling выключен (default true)
 * @param {boolean} opts.runImmediately - запустить tick сразу при mount
 *   (default true)
 * @param {number} opts.maxBackoffMs - cap для exponential backoff
 *   (default 300_000 = 5 минут)
 */
export function usePolling(callback, intervalMs, opts = {}) {
  const {
    enabled = true,
    runImmediately = true,
    maxBackoffMs = 5 * 60 * 1000,
  } = opts

  // Refs чтобы изменения callback/опций не дёргали restart polling
  const callbackRef = useRef(callback)
  const failCountRef = useRef(0)
  const timerRef = useRef(null)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    if (!enabled) return

    let cancelled = false

    const computeDelay = () => {
      if (failCountRef.current === 0) return intervalMs
      // exponential backoff: base × 2^fails, capped
      const backed = intervalMs * Math.pow(2, failCountRef.current)
      return Math.min(backed, maxBackoffMs)
    }

    const tick = async () => {
      if (cancelled) return

      // Skip если offline — нет смысла бить мёртвую сеть
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        // Перезаписываем таймер на base interval — когда сеть вернётся,
        // 'online' event сразу дёрнет poll, плюс через intervalMs тоже
        timerRef.current = setTimeout(tick, intervalMs)
        return
      }

      try {
        await callbackRef.current()
        failCountRef.current = 0  // success — сбрасываем backoff
      } catch {
        failCountRef.current += 1
      }

      if (cancelled) return
      timerRef.current = setTimeout(tick, computeDelay())
    }

    // Когда сеть вернулась — сбрасываем backoff и сразу дёргаем tick
    const handleOnline = () => {
      failCountRef.current = 0
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      tick()
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline)
    }

    if (runImmediately) {
      tick()
    } else {
      timerRef.current = setTimeout(tick, intervalMs)
    }

    return () => {
      cancelled = true
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline)
      }
    }
  }, [enabled, intervalMs, maxBackoffMs, runImmediately])
}
